import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Plus, Save, Edit3, Trash2, Play, Image, FileText, ExternalLink, X } from "lucide-react";

const convertToEmbedUrl = (url) => {
  if (!url) return "";
  if (url.includes("/embed/") || url.includes("/preview")) return url;
  const driveMatch = url.match(/\/d\/([^\/]+)/);
  if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) videoId = watchMatch[1];
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) videoId = shortMatch[1];
  const shortsMatch = url.match(/shorts\/([^?&]+)/);
  if (shortsMatch) videoId = shortsMatch[1];
  if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  return url;
};

export default function StrumentiSubcategoria() {
  const { slug } = useParams();
  const { isAdmin } = useAuth();
  const [subcategory, setSubcategory] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addType, setAddType] = useState("image");
  const [newMedia, setNewMedia] = useState({ title: "", url: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", url: "" });
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => { fetchContent(); }, [slug]);

  const fetchContent = async () => {
    try {
      const [subsRes, mediaRes] = await Promise.all([
        axios.get(`${API}/content/strumenti-subcategories`),
        axios.get(`${API}/content/strumenti-media`)
      ]);
      const allSubs = subsRes.data.map(s => { try { return { ...s, ...JSON.parse(s.content) }; } catch { return s; } });
      setSubcategory(allSubs.find(s => s.slug === slug));
      setMedia(mediaRes.data.filter(m => {
        try { const p = JSON.parse(m.content); return p.subcategory === slug; }
        catch { return false; }
      }).sort((a, b) => a.order - b.order));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const addMedia = async () => {
    if (!newMedia.url.trim()) { toast.error("Inserisci un URL"); return; }
    const url = addType === "video" ? convertToEmbedUrl(newMedia.url) : newMedia.url;
    try {
      await axios.post(`${API}/content`, {
        section: "strumenti-media",
        content_type: addType,
        title: newMedia.title || (addType === "video" ? "Video" : addType === "doc" ? "Documento" : "Immagine"),
        content: JSON.stringify({ url, subcategory: slug, type: addType }),
        order: media.length
      });
      toast.success("Aggiunto");
      setNewMedia({ title: "", url: "" });
      setShowAdd(false);
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  const saveEdit = async (id) => {
    try {
      const item = media.find(m => m.id === id);
      const parsed = JSON.parse(item.content);
      const url = parsed.type === "video" ? convertToEmbedUrl(editForm.url) : editForm.url;
      await axios.put(`${API}/content/${id}`, {
        title: editForm.title,
        content: JSON.stringify({ ...parsed, url })
      });
      toast.success("Aggiornato");
      setEditingId(null);
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  const deleteMedia = async (id) => {
    try { await axios.delete(`${API}/content/${id}`); toast.success("Eliminato"); fetchContent(); }
    catch { toast.error("Errore"); }
  };

  const getMediaData = (item) => {
    try { return JSON.parse(item.content); } catch { return { url: item.content, type: "image" }; }
  };

  if (loading) {
    return <div className="page-container flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} /></div>;
  }

  return (
    <div className="page-container" data-testid="strumenti-subcategoria-page">
      <PageHero image={HERO_IMAGES.strumenti} kicker="Strumenti" title={subcategory?.title || slug} subtitle={subcategory?.description} />
      <SectionBg type="crypto" />
      <div className="container py-8 relative">
        <Link to="/strumenti" className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-lg transition-all hover:bg-white/5" style={{ color: "var(--mm-gold)" }}>
          <ArrowLeft size={20} /><span style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Torna agli Strumenti</span>
        </Link>

        {/* Admin Add */}
        {isAdmin && (
          <div className="mb-8 max-w-lg mx-auto">
            {!showAdd ? (
              <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2 mx-auto" data-testid="add-media-btn">
                <Plus size={18} /> Aggiungi Contenuto
              </button>
            ) : (
              <div className="glass-card p-5 space-y-4">
                <div className="flex gap-2">
                  <button onClick={() => setAddType("image")} className="flex-1 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all" style={{ backgroundColor: addType === "image" ? "rgba(47,216,168,0.15)" : "rgba(255,255,255,0.03)", border: addType === "image" ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)", color: addType === "image" ? "var(--mm-gold)" : "var(--mm-text-secondary)", fontFamily: "'Space Grotesk', sans-serif" }}>
                    <Image size={16} /> FOTO
                  </button>
                  <button onClick={() => setAddType("video")} className="flex-1 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all" style={{ backgroundColor: addType === "video" ? "rgba(47,216,168,0.15)" : "rgba(255,255,255,0.03)", border: addType === "video" ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)", color: addType === "video" ? "var(--mm-gold)" : "var(--mm-text-secondary)", fontFamily: "'Space Grotesk', sans-serif" }}>
                    <Play size={16} /> VIDEO
                  </button>
                  <button onClick={() => setAddType("doc")} className="flex-1 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all" style={{ backgroundColor: addType === "doc" ? "rgba(47,216,168,0.15)" : "rgba(255,255,255,0.03)", border: addType === "doc" ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)", color: addType === "doc" ? "var(--mm-gold)" : "var(--mm-text-secondary)", fontFamily: "'Space Grotesk', sans-serif" }} data-testid="add-type-doc">
                    <FileText size={16} /> DOCUMENTO
                  </button>
                </div>
                <input type="text" value={newMedia.title} onChange={(e) => setNewMedia({ ...newMedia, title: e.target.value })} className="admin-input" placeholder="Titolo (opzionale)" />
                <input type="text" value={newMedia.url} onChange={(e) => setNewMedia({ ...newMedia, url: e.target.value })} className="admin-input" placeholder={addType === "video" ? "Link YouTube o Google Drive" : addType === "doc" ? "Link documento (es. Fogli/Docs Google)" : "URL immagine"} data-testid="media-url-input" />
                <div className="flex gap-3">
                  <button onClick={addMedia} className="btn-primary flex-1"><Save size={14} className="inline mr-1" />Salva</button>
                  <button onClick={() => { setShowAdd(false); setNewMedia({ title: "", url: "" }); }} className="btn-secondary flex-1">Annulla</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Media Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {media.map((item, index) => {
            const data = getMediaData(item);
            if (editingId === item.id) {
              return (
                <div key={item.id} className="glass-card p-4 space-y-3">
                  <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="admin-input" placeholder="Titolo" />
                  <input type="text" value={editForm.url} onChange={(e) => setEditForm({ ...editForm, url: e.target.value })} className="admin-input" placeholder="URL" />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(item.id)} className="btn-primary flex-1 py-2 text-sm">Salva</button>
                    <button onClick={() => setEditingId(null)} className="btn-secondary flex-1 py-2 text-sm">Annulla</button>
                  </div>
                </div>
              );
            }
            return (
              <div key={item.id} className="glass-card overflow-hidden group" data-testid={`media-${index}`}>
                {data.type === "video" ? (
                  <div className="video-container"><iframe src={data.url} title={item.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen frameBorder="0" /></div>
                ) : data.type === "doc" ? (
                  <a href={data.url} target="_blank" rel="noopener noreferrer" className="aspect-video flex flex-col items-center justify-center gap-3 transition-colors hover:bg-white/5" style={{ backgroundColor: "rgba(47,216,168,0.05)", textDecoration: "none" }} data-testid={`doc-link-${index}`}>
                    <FileText size={44} style={{ color: "var(--mm-gold)" }} />
                    <span className="flex items-center gap-1.5 text-sm font-bold uppercase" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.06em" }}>
                      Apri documento <ExternalLink size={14} />
                    </span>
                  </a>
                ) : (
                  <div className="aspect-video overflow-hidden cursor-pointer" onClick={() => setLightbox(data.url)}>
                    <img src={data.url} alt={item.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  </div>
                )}
                <div className="p-3 flex items-center justify-between">
                  <h3 className="text-sm sm:text-base font-semibold truncate" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>{item.title}</h3>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => { setEditingId(item.id); setEditForm({ title: item.title, url: data.url }); }} className="p-1.5 rounded" style={{ backgroundColor: "rgba(47,216,168,0.1)" }}><Edit3 size={14} style={{ color: "var(--mm-gold)" }} /></button>
                      <button onClick={() => deleteMedia(item.id)} className="p-1.5 rounded" style={{ backgroundColor: "rgba(239,68,68,0.1)" }}><Trash2 size={14} style={{ color: "#EF4444" }} /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {media.length === 0 && (
          <div className="glass-card p-12 text-center max-w-md mx-auto">
            <Image size={48} style={{ color: "var(--mm-gold)", margin: "0 auto 16px" }} />
            <p style={{ color: "var(--mm-text-secondary)" }}>{isAdmin ? "Aggiungi foto, video o documenti" : "Contenuti in arrivo"}</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" onClick={() => setLightbox(null)}><X size={24} style={{ color: "white" }} /></button>
          <img src={lightbox} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}
